import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles, Trash2, Calendar, Eye } from "lucide-react";
import { format } from "date-fns";

interface TemplateCardProps {
  template: {
    _id: string;
    name: string;
    description?: string;
    createdAt: number;
    typstContent: string;
  };
  onUseTemplate: (template: { _id: string; name: string; description?: string }) => void;
  onPreview: (template: {
    _id: string;
    name: string;
    description?: string;
    typstContent: string;
  }) => void;
  onDelete: (templateId: string) => void;
}

/**
 * Card component for displaying a user document template
 * Shows template name, description, and action buttons
 */
export function TemplateCard({ template, onUseTemplate, onPreview, onDelete }: TemplateCardProps) {
  return (
    <Card className="group flex flex-col transition-shadow duration-200 hover:shadow-lg">
      <CardContent className="flex-1 pt-6">
        <div className="mb-3 flex items-start gap-3">
          <div className="shrink-0 rounded-lg bg-purple-100 p-2.5">
            <FileText className="h-5 w-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-semibold text-gray-900">{template.name}</h3>
            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="h-3 w-3" />
              <span>{format(new Date(template.createdAt), "MMM d, yyyy")}</span>
            </div>
          </div>
        </div>

        {template.description && (
          <p className="line-clamp-2 text-sm text-gray-600">{template.description}</p>
        )}

        {!template.description && <p className="text-sm italic text-gray-400">No description</p>}
      </CardContent>

      <CardFooter className="flex gap-2 pb-4 pt-0">
        <Button onClick={() => onPreview(template)} variant="outline" size="sm" className="gap-1.5">
          <Eye className="h-4 w-4" />
          Preview
        </Button>
        <Button
          onClick={() => onUseTemplate(template)}
          size="sm"
          className="flex-1 gap-2 bg-purple-600 hover:bg-purple-700"
        >
          <Sparkles className="h-4 w-4" />
          Use
        </Button>
        <Button
          onClick={() => onDelete(template._id)}
          variant="outline"
          size="sm"
          className="text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
